import { qs } from './utils.js';

/** @typedef {{recoverable: boolean, code: 'read-failed'|'corrupt-json'|'invalid-state'|'storage-unavailable'|'quota-exceeded'|'write-failed'|'remove-failed', operation: 'probe'|'read'|'write'|'remove', key: string|null, cause: unknown|null}} StorageIssue */

const ISSUE_STATES = {
  unavailable: {
    eyebrow: 'SAVING PAUSED',
    title: 'Progress won’t be saved',
    message:
      'Your browser isn’t allowing this app to store changes. Keep this tab open, or export your progress before leaving.',
    actions: ['retry-storage', 'export'],
  },
  unsaved: {
    eyebrow: 'SAVING PAUSED',
    title: 'Your latest change wasn’t saved',
    message: 'Storage is full or unavailable. Your workout is still updated in this tab.',
    actions: ['retry-storage', 'export'],
  },
  recovery: {
    eyebrow: 'RECOVERY NEEDED',
    title: 'Your saved progress can’t be read',
    message:
      'We haven’t changed the saved data. Import a backup, download the unreadable data, or start again.',
    actions: ['import-label', 'import', 'download-damaged', 'discard-damaged'],
  },
  readFailed: {
    eyebrow: 'SAVING PAUSED',
    title: 'Progress couldn’t be read',
    message: 'Saved progress could not be read. You can keep using this session and try again.',
    actions: ['retry-storage', 'export'],
  },
  writeFailed: {
    eyebrow: 'SAVING PAUSED',
    title: 'Your latest change wasn’t saved',
    message: 'Progress could not be saved. You can keep using this session and try again.',
    actions: ['retry-storage', 'export'],
  },
  removeFailed: {
    eyebrow: 'SAVING PAUSED',
    title: 'Saved progress couldn’t be reset',
    message: 'Saved progress could not be reset. Your current session has still been updated.',
    actions: ['retry-storage', 'export'],
  },
};

export default class FeedbackUI {
  /** Creates recovery, toast, and confirmation UI below a document or element root. @param {Document|Element} [root=document] */
  constructor(root = document) {
    this.root = root;
    this.status = qs('#storage-status', root);
    this.notificationRegion = qs('#notification-region', root);
    this.dialog = qs('#progress-dialog', root);
    this.issueSignature = null;
    this.toast = null;
    this.toastTimer = null;
    this.toastRemaining = 0;
    this.toastStartedAt = 0;
    this.toastPaused = false;
    this.toastPauseReasons = new Set();
    this.toastExitTimer = null;
    this.leavingToast = null;
    this.pendingDialog = null;
    this.queuedDialog = null;
    this.dialogClosing = false;
    this.dialogCloseFallback = null;
    this.closingCleanup = null;
    this.isWired = false;
    this.recoveryFocusSnapshot = null;
    this.recoveryFocusoutHandler = null;
    this.onStorageIssue = (event) => this.showIssue(event.detail);
    this.onStorageRecovered = () => {
      this.clearIssue();
      this.showToast('Saving restored');
    };
    this.onVisibilityChange = () => {
      if (!this.toast) return;
      this.setToastPauseReason('hidden', document.hidden);
    };
  }

  /** Wires idempotent storage issue and recovery event listeners. @returns {void} */
  wireStorageEvents() {
    if (this.isWired) return;
    this.root.addEventListener('storage:issue', this.onStorageIssue);
    this.root.addEventListener('storage:recovered', this.onStorageRecovered);
    this.isWired = true;
  }

  /** Displays recovery guidance for a normalized storage issue. @param {Partial<StorageIssue>} [issue] @returns {void} */
  showIssue(issue = {}) {
    const signature = `${issue.code ?? ''}:${issue.operation ?? ''}:${issue.key ?? ''}`;
    if (signature === this.issueSignature) return;
    this.issueSignature = signature;
    if (!this.status) return;

    const state = this.getIssueState(issue);
    const eyebrow = qs('[data-storage-eyebrow]', this.status);
    const title = qs('[data-storage-title]', this.status);
    const message = qs('[data-storage-message]', this.status);
    if (eyebrow) eyebrow.textContent = state.eyebrow;
    if (title) title.textContent = state.title;
    if (message) message.textContent = state.message;
    this.status
      .querySelectorAll(
        '[data-progress-action], [data-recovery-import-label], [data-progress-import]'
      )
      .forEach((action) => {
        action.hidden = !state.actions.includes(this.getControlName(action));
      });
    this.status.hidden = false;
  }

  /** Hides the current storage issue notice, moving focus out of it first. @returns {void} */
  clearIssue() {
    this.issueSignature = null;
    if (!this.status) return;
    // Hiding a subtree that still owns focus strands it on nothing, so the
    // transfer has to complete while the notice is still focusable.
    const ownsFocus = this.status.contains(document.activeElement);
    const confirmationOpen = !!this.pendingDialog || !!qs('#progress-dialog[open]', document);
    if (ownsFocus && !confirmationOpen) this.focusAfterRecovery();
    this.status.hidden = true;
  }

  /** Moves focus to the first candidate that actually accepts it after recovery. @returns {boolean} */
  focusAfterRecovery() {
    this.restoreTemporaryFocusTarget();
    const candidates = [qs('#page-title', document), qs('#main-content', document), document.body];
    return candidates.some((candidate) => this.focusCandidate(candidate));
  }

  /** Focuses one candidate, lending it a temporary tabindex only while it holds focus. @param {HTMLElement|null} candidate @returns {boolean} */
  focusCandidate(candidate) {
    if (!candidate?.isConnected || typeof candidate.focus !== 'function') return false;
    const needsTabindex = !candidate.hasAttribute('tabindex');
    if (needsTabindex) {
      this.recoveryFocusSnapshot = { target: candidate, present: false, value: null };
      candidate.setAttribute('tabindex', '-1');
    }
    candidate.focus({ preventScroll: true });
    if (document.activeElement !== candidate) {
      if (needsTabindex) this.restoreTemporaryFocusTarget({ force: true });
      return false;
    }
    if (needsTabindex) {
      this.recoveryFocusoutHandler = () => this.restoreTemporaryFocusTarget({ force: true });
      candidate.addEventListener('focusout', this.recoveryFocusoutHandler, { once: true });
    }
    return true;
  }

  /** Restores a lent tabindex once its target no longer needs it. @param {{force?: boolean}} [options] @returns {void} */
  restoreTemporaryFocusTarget({ force = false } = {}) {
    const snapshot = this.recoveryFocusSnapshot;
    if (!snapshot) return;
    if (!force && document.activeElement === snapshot.target) return;
    if (this.recoveryFocusoutHandler) {
      snapshot.target.removeEventListener('focusout', this.recoveryFocusoutHandler);
      this.recoveryFocusoutHandler = null;
    }
    if (snapshot.present) snapshot.target.setAttribute('tabindex', snapshot.value);
    else snapshot.target.removeAttribute('tabindex');
    this.recoveryFocusSnapshot = null;
  }

  /** Shows an interruptible status or alert toast. @param {string} message @param {{tone?: 'status'|'alert', duration?: number}} [options] @returns {void} */
  showToast(message, { tone = 'status', duration = 5000 } = {}) {
    this.removeToast({ immediate: true });
    if (!this.notificationRegion) return;

    const toast = document.createElement('div');
    toast.className = 'notification-toast';
    toast.setAttribute('role', tone === 'alert' ? 'alert' : 'status');
    // The host stays neutral; each toast is its own atomic live region so a
    // replacement message is announced in full instead of as a diff.
    toast.setAttribute('aria-atomic', 'true');
    toast.textContent = message;
    toast.addEventListener('mouseenter', () => this.setToastPauseReason('hover', true));
    toast.addEventListener('mouseleave', () => this.setToastPauseReason('hover', false));
    toast.addEventListener('focusin', () => this.setToastPauseReason('focus', true));
    toast.addEventListener('focusout', () => this.setToastPauseReason('focus', false));
    this.notificationRegion.appendChild(toast);
    this.toast = toast;
    this.toastRemaining = Math.max(0, duration);
    this.setToastPauseReason('hidden', document.hidden);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    if (this.toastRemaining > 0 && !this.toastPaused) this.startToastTimer();
  }

  /** Shows one serialized confirmation dialog, falling back to native confirm when needed. @param {{title?: string, message?: string, confirmLabel?: string, invoker?: Element}} [options] @returns {Promise<boolean>} */
  confirm({ title, message, confirmLabel = 'Continue', invoker } = {}) {
    let invocationTarget = invoker ?? document.activeElement;
    if (this.isInsideDialog(invocationTarget)) {
      invocationTarget = this.pendingDialog?.invoker ?? document.body;
    }
    if (!this.dialog || typeof this.dialog.showModal !== 'function') {
      if (typeof window.confirm === 'function') this.announceConfirmationOpening();
      const answer = typeof window.confirm === 'function' ? window.confirm(message) : false;
      this.restoreFocus(invocationTarget);
      return Promise.resolve(answer);
    }

    return new Promise((resolve) => {
      const request = { title, message, confirmLabel, invoker: invocationTarget, resolve };
      if (this.pendingDialog || this.dialogClosing) {
        // Keep one newest request; a superseded queued confirmation resolves false.
        if (this.queuedDialog) this.queuedDialog.resolve(false);
        this.queuedDialog = request;
        if (this.pendingDialog) this.settleDialog(false, { restoreFocus: false });
        return;
      }
      this.openDialog(request);
    });
  }

  openDialog({ title, message, confirmLabel, invoker, resolve }) {
    const titleElement = qs('#progress-dialog-title', this.dialog);
    const messageElement = qs('#progress-dialog-message', this.dialog);
    const confirmButton = qs('[data-dialog-confirm]', this.dialog);
    const cancelButton = qs('[data-dialog-cancel]', this.dialog);
    if (titleElement) titleElement.textContent = title;
    if (messageElement) messageElement.textContent = message;
    if (confirmButton) confirmButton.textContent = confirmLabel;

    const onConfirm = (event) => {
      event.preventDefault();
      this.settleDialog(true);
    };
    const onCancelButton = (event) => {
      event.preventDefault();
      this.settleDialog(false);
    };
    const onCancel = (event) => {
      event.preventDefault();
      this.settleDialog(false);
    };
    const onClose = () => {
      if (this.dialogClosing) this.finishDialogClose();
      else this.settleDialog(this.dialog.returnValue === 'confirm', { close: false });
    };
    const onBackdrop = (event) => {
      if (event.target !== this.dialog) return;
      const { left, right, top, bottom } = this.dialog.getBoundingClientRect();
      const hasBounds = right > left && bottom > top;
      const isOutside =
        event.clientX < left ||
        event.clientX > right ||
        event.clientY < top ||
        event.clientY > bottom;
      if (!hasBounds || isOutside) this.settleDialog(false);
    };
    this.pendingDialog = {
      invoker,
      resolve,
      cleanup: ({ keepClose = false } = {}) => {
        confirmButton?.removeEventListener('click', onConfirm);
        cancelButton?.removeEventListener('click', onCancelButton);
        this.dialog.removeEventListener('cancel', onCancel);
        if (!keepClose) this.dialog.removeEventListener('close', onClose);
        this.dialog.removeEventListener('click', onBackdrop);
      },
    };
    confirmButton?.addEventListener('click', onConfirm);
    cancelButton?.addEventListener('click', onCancelButton);
    this.dialog.addEventListener('cancel', onCancel);
    this.dialog.addEventListener('close', onClose);
    this.dialog.addEventListener('click', onBackdrop);
    try {
      // Signalled at the real opening only, never when a request is queued, so
      // a running timer takeover releases modality exactly once.
      this.announceConfirmationOpening();
      this.dialog.showModal();
      cancelButton?.focus();
    } catch {
      this.settleDialog(false, { close: false });
    }
  }

  /** Removes listeners, timers, and unresolved dialog state. @returns {void} */
  destroy() {
    this.root.removeEventListener('storage:issue', this.onStorageIssue);
    this.root.removeEventListener('storage:recovered', this.onStorageRecovered);
    this.isWired = false;
    this.removeToast({ immediate: true });
    if (this.pendingDialog) this.settleDialog(false);
    if (this.queuedDialog) this.queuedDialog.resolve(false);
    this.queuedDialog = null;
    if (this.dialogCloseFallback) window.clearTimeout(this.dialogCloseFallback);
    this.dialogCloseFallback = null;
    this.closingCleanup?.();
    this.closingCleanup = null;
    this.dialogClosing = false;
    this.restoreTemporaryFocusTarget({ force: true });
  }

  /** Announces that a confirmation is opening right now. @returns {void} */
  announceConfirmationOpening() {
    document.dispatchEvent(new CustomEvent('confirmation:opening'));
  }

  getIssueState(issue) {
    if (['corrupt-json', 'invalid-state'].includes(issue.code)) return ISSUE_STATES.recovery;
    if (issue.code === 'read-failed') return ISSUE_STATES.readFailed;
    if (issue.code === 'remove-failed') return ISSUE_STATES.removeFailed;
    if (issue.code === 'quota-exceeded') return ISSUE_STATES.unsaved;
    if (issue.code === 'write-failed') return ISSUE_STATES.writeFailed;
    return ISSUE_STATES.unavailable;
  }

  startToastTimer() {
    if (!this.toast || this.toastRemaining <= 0 || this.toastPaused) return;
    this.toastStartedAt = Date.now();
    this.toastTimer = window.setTimeout(() => this.removeToast(), this.toastRemaining);
  }

  // A Set composes hidden, hover, and focus pauses; countdown resumes only when empty.
  setToastPauseReason(reason, shouldPause) {
    if (shouldPause) this.toastPauseReasons.add(reason);
    else this.toastPauseReasons.delete(reason);
    if (this.toastPauseReasons.size > 0) this.pauseToast();
    else this.resumeToast();
  }

  pauseToast() {
    if (!this.toast || this.toastPaused) return;
    if (this.toastTimer) {
      window.clearTimeout(this.toastTimer);
      this.toastTimer = null;
      this.toastRemaining = Math.max(0, this.toastRemaining - (Date.now() - this.toastStartedAt));
    }
    this.toastPaused = true;
  }

  resumeToast() {
    if (!this.toast || !this.toastPaused || this.toastPauseReasons.size > 0) return;
    this.toastPaused = false;
    this.startToastTimer();
  }

  /** Removes the current toast immediately or with a motion-aware fade. @param {{immediate?: boolean}} [options] @returns {void} */
  removeToast({ immediate = false } = {}) {
    if (immediate && this.leavingToast) {
      this.leavingToast.remove();
      this.leavingToast = null;
      if (this.toastExitTimer) window.clearTimeout(this.toastExitTimer);
      this.toastExitTimer = null;
    }
    if (this.toastTimer) window.clearTimeout(this.toastTimer);
    this.toastTimer = null;
    const toast = this.toast;
    this.toast = null;
    this.toastRemaining = 0;
    this.toastPaused = false;
    this.toastPauseReasons.clear();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (!toast) return;
    if (!immediate && this.supportsToastFade()) {
      toast.classList.add('is-leaving');
      this.leavingToast = toast;
      if (this.toastExitTimer) window.clearTimeout(this.toastExitTimer);
      this.toastExitTimer = window.setTimeout(() => this.finishToastExit(toast), 150);
      return;
    }
    toast.remove();
  }

  // CSS supplies the fade; this only respects a reduced-motion preference.
  supportsToastFade() {
    return (
      typeof window.matchMedia === 'function' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  finishToastExit(toast) {
    if (this.leavingToast !== toast) return;
    toast.remove();
    this.leavingToast = null;
    this.toastExitTimer = null;
  }

  // Native close advances the queue; a zero-delay fallback covers browsers that omit it.
  settleDialog(answer, { close = true, restoreFocus = true } = {}) {
    const pending = this.pendingDialog;
    if (!pending) return;
    this.pendingDialog = null;
    const shouldClose = close && this.dialog?.open && typeof this.dialog.close === 'function';
    if (shouldClose) {
      this.dialogClosing = true;
      pending.cleanup({ keepClose: true });
      this.closingCleanup = () => pending.cleanup();
      this.dialog.close(answer ? 'confirm' : 'cancel');
      if (this.dialogClosing) {
        this.dialogCloseFallback = window.setTimeout(() => this.finishDialogClose(), 0);
      }
    } else {
      pending.cleanup();
    }
    if (restoreFocus) this.restoreFocus(pending.invoker);
    pending.resolve(answer);
    if (!shouldClose) this.openQueuedDialog();
  }

  finishDialogClose() {
    if (!this.dialogClosing) return;
    this.dialogClosing = false;
    if (this.dialogCloseFallback) window.clearTimeout(this.dialogCloseFallback);
    this.dialogCloseFallback = null;
    this.closingCleanup?.();
    this.closingCleanup = null;
    this.openQueuedDialog();
  }

  openQueuedDialog() {
    const request = this.queuedDialog;
    if (!request || this.pendingDialog || this.dialogClosing) return;
    this.queuedDialog = null;
    this.openDialog(request);
  }

  restoreFocus(invoker) {
    if (invoker?.isConnected && typeof invoker.focus === 'function') invoker.focus();
  }

  getControlName(control) {
    if (control.hasAttribute('data-recovery-import-label')) return 'import-label';
    if (control.hasAttribute('data-progress-import')) return 'import';
    return control.dataset.progressAction;
  }

  isInsideDialog(element) {
    return !!(this.dialog && element && this.dialog.contains(element));
  }
}
